import React, { useState, useRef } from 'react';
import { Accordion, Modal } from 'react-bootstrap';
import { HOST } from '../general/utils';
import diagramSrc from '../../images/Diagrama flujo de negocios.png';
import diagramCicloSrc from '../../images/Diagrama ciclo de vida de un pedido.png';
import infografiaCrearPedidoImg from '../../images/InfografiaCrearPedido.png';

const FAQ = [
    {
        q: '¿Qué es Abi?',
        a: 'Solución SaaS para la gestión inteligente de suministros vinculados a presupuestos. Centraliza la trazabilidad de pedidos, genera reportes de ejecución en tiempo real y mantiene un control riguroso de los techos financieros.',
    },
    {
        q: '¿Qué ventajas ofrece Abi?',
        a: 'Elimina la "deuda técnica administrativa" mediante el control detallado de centros de costos y fondos. Simplifica la auditoría de procesos y automatiza la generación de reportes para la toma de decisiones estratégicas.',
    },
    {
        q: '¿Abi es compatible con dispositivos móviles?',
        a: 'Sí. El sistema ha sido desarrollado bajo un estándar responsivo (Mobile-First), permitiendo el acceso completo a todas las funcionalidades desde smartphones, tablets y equipos de escritorio.',
    },
    {
        q: '¿Cómo puedo agregar un nuevo usuario?',
        a: 'El proceso inicia con el registro del colaborador en la plataforma. Posteriormente, un administrador debe validar el perfil y asignar los permisos y fondos específicos desde el panel de Configuración de Usuarios.',
    },
    {
        q: '¿Cómo se asignan los fondos a los usuarios?',
        a: 'La distribución presupuestal se gestiona en Configuración → Fondos. Allí se definen los límites de gasto por usuario o centro de costos para asegurar que no se excedan los presupuestos aprobados.',
    },
    {
        q: '¿Qué tipo de reportes puedo generar en Abi?',
        a: 'Abi permite generar informes de ejecución presupuestal por contrato, distribución por centros de costos y logs de seguimiento de pedidos. Todos los datos son exportables en Excel para auditorías contables.',
    },
];

const DOCS = [
    {
        icon: 'fa-file-pdf-o',
        label: '¿Cómo crear y gestionar un pedido?',
        href: HOST + 'storage/files/createOrder.pdf',
    },
    {
        icon: 'fa-file-pdf-o',
        label: '¿Cómo asignar techos presupuestales a usuarios nuevos?',
        href: HOST + 'storage/files/addBudgetToUser.pdf',
    },
];

const DIAGRAMS = [
    { src: diagramSrc,      title: 'Diagrama Configuracion para hacer pedidos' },
    { src: diagramCicloSrc, title: 'Diagrama ciclo de vida de un pedido' },
    { src: infografiaCrearPedidoImg, title: 'Infografía crear pedido' },
];

function DiagramCard({ src, title, onOpen }) {
    return (
        <div className="help-diagram-card" onClick={onOpen}>
            <div className="help-diagram-preview">
                <img src={src} alt={title} className="help-diagram-thumb" />
                <div className="help-diagram-overlay">
                    <i className="fa fa-search-plus" style={{ fontSize: 32 }} />
                    <span style={{ marginTop: 8, fontSize: 13, fontWeight: 600 }}>Clic para ampliar</span>
                </div>
            </div>
            <div className="help-diagram-footer">
                <span className="help-doc-icon"><i className="fa fa-sitemap" /></span>
                <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{title}</span>
                <i className="fa fa-expand" style={{ color: 'var(--abi-muted)', fontSize: 13 }} />
            </div>
        </div>
    );
}

function DiagramModal({ src, title, show, onHide }) {
    const [zoom, setZoom] = useState(1);
    const scrollRef = useRef(null);
    const changeZoom = (delta) => setZoom(z => Math.min(4, Math.max(0.5, parseFloat((z + delta).toFixed(2)))));

    return (
        <Modal show={show} onHide={onHide} dialogClassName="modal-diagram" centered onShow={() => setZoom(1)}>
            <Modal.Header closeButton style={{ padding: '10px 16px', gap: 12 }}>
                <Modal.Title style={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <i className="fa fa-sitemap" style={{ color: 'var(--abi-primary)' }} />
                    {title}
                </Modal.Title>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', marginRight: 8 }}>
                    <div className="print-font-stepper">
                        <button onClick={() => changeZoom(-0.25)} title="Reducir">−</button>
                        <span>{Math.round(zoom * 100)}%</span>
                        <button onClick={() => changeZoom(+0.25)} title="Ampliar">+</button>
                    </div>
                    <button className="report-icon-btn" onClick={() => setZoom(1)} title="Restablecer zoom">
                        <i className="fa fa-refresh" />
                    </button>
                </div>
            </Modal.Header>
            <Modal.Body style={{ padding: 0, background: '#e2e8f0', overflow: 'auto', maxHeight: '85vh' }} ref={scrollRef}>
                <div style={{ width: `${zoom * 100}%`, minWidth: '100%', padding: 20, boxSizing: 'border-box' }}>
                    <img src={src} alt={title} style={{ width: '100%', display: 'block', borderRadius: 4, boxShadow: '0 4px 24px rgba(15,23,42,0.18)' }} />
                </div>
            </Modal.Body>
        </Modal>
    );
}

function Help() {
    const [activeDiagram, setActiveDiagram] = useState(null);

    return (
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
            {/* Hero */}
            <div className="help-hero">
                <div className="help-hero-icon"><i className="fa fa-life-ring" /></div>
                <div>
                    <div className="help-hero-title">Centro de Ayuda ABI</div>
                    <div className="help-hero-sub">
                        Bienvenido a la asistencia de <strong>ABI</strong>. Encuentra respuestas rápidas y accede a los instructivos técnicos del sistema.
                    </div>
                </div>
            </div>


            {/* FAQ */}
            <div style={{ marginBottom: 28 }}>
                <div className="help-section-label">Preguntas frecuentes</div>
                <Accordion flush className="help-accordion">
                    {FAQ.map((item, i) => (
                        <Accordion.Item key={i} eventKey={String(i)} className="help-faq-item">
                            <Accordion.Header>{item.q}</Accordion.Header>
                            <Accordion.Body>{item.a}</Accordion.Body>
                        </Accordion.Item>
                    ))}
                </Accordion>
            </div>

            {/* Diagramas */}
            <div style={{ marginBottom: 28 }}>
                <div className="help-section-label">Diagramas</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
                    {DIAGRAMS.map(d => (
                        <DiagramCard key={d.title} src={d.src} title={d.title} onOpen={() => setActiveDiagram(d)} />
                    ))}
                </div>
            </div>

            {/* Docs */}
            <div>
                <div className="help-section-label">Instructivos técnicos</div>
                {DOCS.map((doc, i) => (
                    <a key={i} href={doc.href} target="_blank" rel="noreferrer" className="help-doc-link">
                        <span className="help-doc-icon"><i className={`fa ${doc.icon}`} /></span>
                        <span style={{ flex: 1 }}>{doc.label}</span>
                        <i className="fa fa-external-link" style={{ color: 'var(--abi-muted)', fontSize: 12 }} />
                    </a>
                ))}
            </div>

            {/* Modal diagrama */}
            <DiagramModal
                show={!!activeDiagram}
                onHide={() => setActiveDiagram(null)}
                src={activeDiagram?.src}
                title={activeDiagram?.title}
            />
        </div>
    );
}

export default Help;
